import {
  defaultMessageForHttpStatus,
  isSystemHttpStatus,
  sanitizeIpeUserMessage,
  shouldPassThroughError,
  toIpeUserErrorMessage,
} from './ipeErrorSanitizer';

// re-export helpers used by resolveUserMessage (isUserFacing via sanitize path)
function isUserFacingMessage(message) {
  const s = String(message || '').trim();
  if (!s) return false;
  return /SHEIN|TEMU|Temu|openapi-b-partner|\/open-api\/|openapi00003|没有该接口访问权限|错误 code=|平台 code=|ipeTrace=|platformTrace=|IPE豆不足|积分不足|店铺授权|无权管理|工作区未验证|查询上架|处置数据|发品资格|不可发品|上架信息|empty size|Does not currently support|size value|category configuration/i.test(s);
}

function appendTraceHint(userMessage, traceId) {
  const msg = String(userMessage || '').trim();
  const tid = String(traceId || '').trim();
  if (!tid || !msg) return msg || userMessage;
  if (msg.includes(tid) || /ipeTrace=/i.test(msg)) return msg;
  return `${msg}（排查码 ${tid}）`;
}

function looksTechnicalStack(message) {
  const s = String(message || '');
  return /NullPointerException|SQLException|at com\.|at org\.|at java\./i.test(s);
}

function looksLikePlatformBusinessEnglish(message) {
  const s = String(message || '').trim();
  if (!s || s.length > 500 || looksTechnicalStack(s)) return false;
  if (/Exception|at com\.|Internal Server Error|BAD_GATEWAY/i.test(s)) return false;
  return /empty size|size value|category configuration|does not currently support|please check|try to submit|required|invalid|not support|permission|access|token|size chart|template/i.test(s);
}

function isTemuApiPath(path) {
  return /\/temu\//i.test(String(path || ''));
}

const SKIP_LOG_PATHS = [
  '/ipe-users/client-error-log',
];

/** 已删除/不存在的刊登任务详情：前端会清理 URL 与本地 meta，不必上报 ClientError。 */
function shouldSkipLog(path, method, status, { network = false } = {}) {
  const p = String(path || '').split('?')[0];
  const m = String(method || '').toUpperCase();
  if (SKIP_LOG_PATHS.some((item) => p.includes(item))) return true;
  if (Number(status) === 404 && m === 'GET' && /\/listing-jobs\/[^/]+$/.test(p)) {
    return true;
  }
  // 本地上传临时 id（LIB-*）未入库，前端不应再查服务端；旧版仍可能打到 404
  if (Number(status) === 404 && m === 'GET' && /\/admin\/image-space\/LIB-[^/]+$/.test(p)) {
    return true;
  }
  // 商品库轮询 getBatch / items：瞬时网络失败极常见，上报会刷屏且干扰排查真实错误
  if (network && m === 'GET' && (
    /\/sourcing\/capture\/batches(?:\/|$)/.test(p)
    || /\/sourcing\/capture\/items(?:\/|$)/.test(p)
  )) {
    return true;
  }
  return false;
}

let logIpeClientErrorFn = null;

function scheduleIpeErrorLog(context, error, userMessage, extra) {
  if (typeof window === 'undefined' || process.env.NODE_ENV === 'test') return;
  if (!logIpeClientErrorFn) {
    import('./ipeClientErrorLogger')
      .then((mod) => {
        logIpeClientErrorFn = mod.logIpeClientError;
        logIpeClientErrorFn(context, error, userMessage, extra);
      })
      .catch(() => {});
    return;
  }
  logIpeClientErrorFn(context, error, userMessage, extra);
}

function buildInterceptedError(source, userMessage) {
  const next = new Error(userMessage);
  next.status = source?.status;
  next.code = source?.code;
  next.payload = source?.payload;
  next.retryAfterSeconds = source?.retryAfterSeconds;
  next.rawMessage = source?.rawMessage || source?.message;
  next.traceId = source?.traceId || source?.payload?.traceId || undefined;
  next.ipeIntercepted = true;
  if (source?.stack) {
    next.stack = source.stack;
  }
  return next;
}

function resolveUserMessage(error, defaultMessage, path = '') {
  if (!error) return defaultMessage;
  if (
    shouldPassThroughError(error)
    || error.code === 'TEMU_API_ERROR'
    || error.code === 'SHOP_BINDING_CONFLICT'
  ) {
    const rawPass = String(error.message || error.rawMessage || '').trim();
    if (rawPass && !looksTechnicalStack(rawPass)) {
      return sanitizeIpeUserMessage(rawPass, defaultMessage);
    }
    return toIpeUserErrorMessage(error, defaultMessage);
  }
  const status = Number(error.status);
  const raw = String(error.message || error.rawMessage || '').trim();
  // Temu 全路径：优先透出平台原文（含英文 errorMsg），勿统一成「服务器繁忙」
  if (isTemuApiPath(path) && raw && !looksTechnicalStack(raw)) {
    return sanitizeIpeUserMessage(raw, defaultMessageForHttpStatus(status) || defaultMessage);
  }
  // 5xx：优先保留平台/中文业务原文，勿统一成「服务器繁忙」
  if (isSystemHttpStatus(status) && raw && !looksTechnicalStack(raw)) {
    if (
      isUserFacingMessage(raw)
      || /SHEIN|TEMU|Temu|openapi-b-partner|\/open-api\/|openapi00003|没有该接口访问权限|错误 code=|平台 code=/i.test(raw)
      || /[\u4e00-\u9fff]/.test(raw)
      || looksLikePlatformBusinessEnglish(raw)
    ) {
      return sanitizeIpeUserMessage(raw, defaultMessageForHttpStatus(status));
    }
    return defaultMessageForHttpStatus(status);
  }
  if (isSystemHttpStatus(status)) {
    return defaultMessageForHttpStatus(status);
  }
  return toIpeUserErrorMessage(error, defaultMessage);
}

/**
 * IPE 全局 HTTP 异常拦截器（等价于前端 AOP）
 * 对系统级 HTTP 错误脱敏、记录原始错误并抛出友好 Error
 */
export function interceptIpeHttpError(error, {
  path = '',
  method = 'GET',
  ipeCall = false,
  defaultMessage = '操作失败，请稍后重试',
} = {}) {
  if (!error) {
    return buildInterceptedError({ message: defaultMessage }, defaultMessage);
  }
  if (error.ipeIntercepted) {
    return error;
  }

  const rawMessage = error.message || defaultMessage;
  const source = {
    ...error,
    message: rawMessage,
    rawMessage,
  };

  const userMessage = appendTraceHint(
    resolveUserMessage(source, defaultMessage, path),
    source.traceId || source.payload?.traceId,
  );
  const shouldLog = ipeCall
    && !shouldSkipLog(path, method, source.status)
    && isSystemHttpStatus(source.status);

  if (shouldLog) {
    scheduleIpeErrorLog(
      `api:${String(method).toUpperCase()}:${path}`,
      source,
      userMessage,
      {
        path,
        method: String(method).toUpperCase(),
        status: source.status,
        code: source.code,
        traceId: source.traceId || source.payload?.traceId,
      },
    );
  }

  return buildInterceptedError(source, userMessage);
}

/**
 * 拦截 fetch 网络层异常（无 HTTP 状态码）
 */
export function interceptIpeNetworkError(error, {
  path = '',
  method = 'GET',
  ipeCall = false,
} = {}) {
  if (!error) {
    return interceptIpeHttpError(new Error('网络异常，请检查网络后重试'), { path, method, ipeCall });
  }
  if (error.ipeIntercepted) {
    return error;
  }

  if (error?.name === 'AbortError' || error?.code === 'TIMEOUT') {
    const timeoutErr = new Error('请求超时，请稍后重试');
    timeoutErr.code = 'TIMEOUT';
    timeoutErr.name = 'AbortError';
    return interceptIpeHttpError(timeoutErr, { path, method, ipeCall, defaultMessage: '请求超时，请稍后重试' });
  }

  const rawMessage = error?.message || 'Network Error';
  const networkErr = new Error(rawMessage);
  networkErr.rawMessage = rawMessage;
  const userMessage = sanitizeIpeUserMessage(rawMessage, '网络异常，请检查网络后重试');

  if (ipeCall && !shouldSkipLog(path, method, undefined, { network: true })) {
    scheduleIpeErrorLog(
      `api:${String(method).toUpperCase()}:${path}`,
      networkErr,
      userMessage,
      { path, method: String(method).toUpperCase(), type: 'network' },
    );
  }

  return buildInterceptedError(networkErr, userMessage);
}

export { sanitizeIpeUserMessage, toIpeUserErrorMessage };
